import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  AutocompleteSearchData,
  BaseField,
  Button,
  DocumentList,
  DocumentsModal,
  Select,
  Spinner,
  TextArea,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { Row, Col } from "react-bootstrap";

import { searchInstitutionNew } from "../../../api/Institution/InstitutionApi";
import {
  getRequest,
  getRequestDocument,
} from "../../../api/Request/RequestApi";
import { useAppDispatch } from "../../../app/store";
import {
  ContentArea,
  FormCard,
  RequestHeader,
  ToolbarEdit,
  ToolbarTop,
} from "../../../components";
import { useAuth } from "../../../hooks/auth/useAuth";
import { ValidationFailure } from "../../../models";
import { InstitutionCodeBookSearch } from "../../../models/Institution";
import {
  DocumentType,
  MzomAppeal,
  MzomAppealOutcomeEnum,
  MzomAppealStatusEnum,
  MzomAppealTypeEnum,
  Request,
} from "../../../models/Request";
import { Attachment } from "../../../models/Shared/Attachment";
import { requestAdminDetailsUrl } from "../../../routes/routes";
import {
  useGetMzomAppealOutcomeListQuery,
  useGetMzomAppealTypeListQuery,
} from "../../../queries/codeBookQuery";
import {
  useGetMzomAppealQuery,
  useSaveMzomAppealMutation,
} from "../../../queries/requestQuery";
import { resetIsEdited, setIsEdited } from "../../../slices/applicationSlice";
import { DEFAULT_REQUEST } from "../constants";
import { canEditMzomAppeal, getMzomAppealStatusSelectData } from "../utils";
import {
  DEFAULT_APPEAL,
  EMPTY_INSTITUTION_SELECT_DATA,
  INSTITUTION_SEARCH_DEFAULTS,
} from "./constants";
import { useMzomAppealFiles } from "./useMzomAppealFiles";
import {
  canAddMzomAppealDocument,
  canSaveMzomAppeal,
  parseOptionalEnumValue,
  toMzomAppealSavePayload,
} from "./utils";

export function MzomAppealEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [appeal, setAppeal] = useState<MzomAppeal>({
    ...DEFAULT_APPEAL,
    requestId: +id,
  });
  const [isEditing, setIsEditing] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();

  const hasRequestId = Boolean(id) && id !== "0";

  const {
    data: mzomAppeal,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetMzomAppealQuery(+id, { skip: !hasRequestId });
  const [saveMzomAppeal, { isLoading: isSaving }] = useSaveMzomAppealMutation();

  const { data: typeList } = useGetMzomAppealTypeListQuery();
  const { data: outcomeList } = useGetMzomAppealOutcomeListQuery();

  const typeSelectData = useMemo(
    () => codeBookToSelectData(typeList ?? []),
    [typeList]
  );
  const statusSelectData = useMemo(() => getMzomAppealStatusSelectData(t), [t]);
  const outcomeSelectData = useMemo(
    () => codeBookToSelectData(outcomeList ?? []),
    [outcomeList]
  );

  useEffect(() => {
    dispatch(resetIsEdited());
  }, [dispatch, id]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }

    if (mzomAppeal) {
      setAppeal(mzomAppeal);
      setIsEditing(false);

      if (mzomAppeal.request) {
        setItem(mzomAppeal.request);
      }

      return;
    }

    (async () => {
      const request = await getRequest(+id);

      if (request) {
        setItem(request);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, mzomAppeal, id]);

  const handleUpdate = (val: MzomAppeal) => {
    setAppeal(val);
    dispatch(setIsEdited());
  };

  const handleEditingClick = () => {
    setIsEditing(true);
  };

  const handleFieldChange = <K extends keyof MzomAppeal>(
    field: K,
    value: MzomAppeal[K]
  ) => {
    handleUpdate({
      ...appeal,
      [field]: value,
    });
  };

  const handleInstitutionSearch = useCallback(
    async (aSearch: AutocompleteSearchData) => {
      let criteria: InstitutionCodeBookSearch | undefined;

      if ((aSearch.text?.length ?? 0) >= 3) {
        criteria = { ...INSTITUTION_SEARCH_DEFAULTS, search: aSearch.text };
      } else if (aSearch.value) {
        const institutionId = Number.parseInt(aSearch.value.toString());

        if (!Number.isNaN(institutionId)) {
          criteria = { ...INSTITUTION_SEARCH_DEFAULTS, ids: [institutionId] };
        }
      }

      if (!criteria) {
        return EMPTY_INSTITUTION_SELECT_DATA;
      }

      return {
        ...EMPTY_INSTITUTION_SELECT_DATA,
        data: await searchInstitutionNew(criteria),
      };
    },
    []
  );

  const handleUploadDocumentClick = () => {
    setShowUploadModal(true);
  };

  const {
    handleFileChange,
    handleUploadFileChange,
    handleRemoveAttachment,
    handleRemoveUploadFile,
  } = useMzomAppealFiles({
    appeal,
    setShowUploadModal,
    handleUpdate,
  });

  const handleDocumentDownload = async (doc: Attachment) => {
    if (!doc?.id) {
      return;
    }

    await getRequestDocument(doc.id, item.id, DocumentType.MzomAppeal);
  };

  const handleSave = async () => {
    const payload = toMzomAppealSavePayload(appeal);

    const result = await saveMzomAppeal(payload).unwrap();

    return result;
  };

  const showEditButton =
    !isEditing && canEditMzomAppeal(item, auth) ? "show" : "hide";

  let showSaveButton: "hide" | "show" | "disable" = "hide";

  if (isEditing) {
    showSaveButton = canSaveMzomAppeal(appeal) ? "show" : "disable";
  }

  if (isLoading || isFetching || isSaving) {
    return <Spinner show />;
  }

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={showEditButton}
        pageTitle={t("mzomAppeal.pageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("common.requestModel.mzomAppeal")}>
        <Row>
          <Col md={6}>
            <Select
              label={t("mzomAppeal.typeLabel")}
              name="typeId"
              value={appeal.typeId}
              selectData={typeSelectData}
              emptyOption={{
                text: "--" + t("common.select") + "--",
                value: undefined,
              }}
              onChange={(e) =>
                handleFieldChange(
                  "typeId",
                  parseOptionalEnumValue<MzomAppealTypeEnum>(e.target.value)
                )
              }
              disabled={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("TypeId"))
                  ?.message
              }
            />
            <Select
              label={t("mzomAppeal.outcomeLabel")}
              name="outcomeId"
              value={appeal.outcomeId}
              selectData={outcomeSelectData}
              emptyOption={{
                text: "--" + t("common.select") + "--",
                value: undefined,
              }}
              onChange={(e) =>
                handleFieldChange(
                  "outcomeId",
                  parseOptionalEnumValue<MzomAppealOutcomeEnum>(e.target.value)
                )
              }
              disabled={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("OutcomeId"))
                  ?.message
              }
            />
            <TextField
              label={t("mzomAppeal.classification")}
              name="classification"
              value={appeal.classification}
              onChange={(e) =>
                handleFieldChange("classification", e.target.value)
              }
              disabled={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("Classification")
                )?.message
              }
            />
          </Col>
          <Col md={6}>
            <Select
              label={t("mzomAppeal.statusLabel")}
              name="status"
              value={appeal.status}
              selectData={statusSelectData}
              emptyOption={{
                text: "--" + t("common.select") + "--",
                value: undefined,
              }}
              onChange={(e) =>
                handleFieldChange(
                  "status",
                  parseOptionalEnumValue<MzomAppealStatusEnum>(e.target.value)
                )
              }
              disabled={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("Status"))
                  ?.message
              }
            />
            {isEditing ? (
              <Autocomplete
                label={t("mzomAppeal.enrolledInstitution")}
                name="enrolledInstitution"
                value={
                  appeal.enrolledInstitutionId > 0
                    ? appeal.enrolledInstitutionId
                    : undefined
                }
                getOptions={handleInstitutionSearch}
                onChange={(e) => {
                  const institutionId = Number.parseInt(e.target.value);

                  handleFieldChange(
                    "enrolledInstitutionId",
                    Number.isNaN(institutionId) ? 0 : institutionId
                  );
                }}
              />
            ) : (
              <TextField
                label={t("mzomAppeal.enrolledInstitution")}
                name="enrolledInstitution"
                value={appeal.enrolledInstitutionName ?? ""}
                disabled={true}
              />
            )}
            {errors?.find((e) =>
              e.propertyName?.startsWith("EnrolledInstitutionId")
            )?.message && (
              <div className="text-danger">
                {
                  errors.find((e) =>
                    e.propertyName?.startsWith("EnrolledInstitutionId")
                  )?.message
                }
              </div>
            )}
            <TextField
              label={t("mzomAppeal.associationNumber")}
              name="associationNumber"
              value={appeal.associationNumber}
              onChange={(e) =>
                handleFieldChange("associationNumber", e.target.value)
              }
              disabled={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("AssociationNumber")
                )?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextArea
              label={t("mzomAppeal.note")}
              ariaLabel={t("mzomAppeal.note")}
              value={appeal.note}
              onChange={(e) =>
                handleFieldChange("note", e.target.value || undefined)
              }
              readonly={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("Note"))?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <BaseField label={t("mzomAppeal.responseLabel")}>
              <Row>
                <Col md={6}>
                  <DocumentList
                    documents={appeal.attachments ?? []}
                    onDocumentClick={handleDocumentDownload}
                    onRemoveClick={handleRemoveAttachment}
                    removable={isEditing}
                  />
                </Col>
                {isEditing && (
                  <Col md={6}>
                    <Button
                      color="primary"
                      onClick={handleUploadDocumentClick}
                      size="sm"
                      disabled={!canAddMzomAppealDocument(appeal)}
                    >
                      {t("request.documentUpload")}
                    </Button>
                    <DocumentList
                      documents={appeal.uploadFiles ?? []}
                      onRemoveClick={handleRemoveUploadFile}
                      removable={isEditing}
                    />
                  </Col>
                )}
              </Row>
            </BaseField>
          </Col>
        </Row>
      </FormCard>
      <DocumentsModal
        title={t("accessibility.documentModal.title")}
        infoPanelText={t("accessibility.documentModal.info")}
        types={t("accessibility.documentModal.types")}
        allDocuments={appeal.uploadFiles ?? []}
        maxSize={10}
        filesAction={handleFileChange}
        open={showUploadModal}
        onConfirm={handleUploadFileChange}
        onClose={() => {
          setShowUploadModal(false);
        }}
        hideOldDocuments={true}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
      <ToolbarEdit
        showCancel={"show"}
        showSave={showSaveButton}
        onClickCancel={history.goBack}
        handleSave={handleSave}
        setSaveErrors={setErrors}
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        showCustom={false}
      />
    </ContentArea>
  );
}
